import React, { useState } from "react";
import { Heart, Sparkles, User, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ArenaParticipant } from "@/hooks/useLiveArenas";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";

interface ParticipantInteractionOverlayProps {
  participant: ArenaParticipant;
  isOpen: boolean;
  onClose: () => void;
  onPop: () => Promise<void>;
  onLike: () => Promise<void>;
  onViewProfile: () => void;
}

export const ParticipantInteractionOverlay: React.FC<ParticipantInteractionOverlayProps> = ({
  participant,
  isOpen,
  onClose,
  onPop,
  onLike,
  onViewProfile,
}) => {
  const [isPopping, setIsPopping] = useState(false);
  const [isLiking, setIsLiking] = useState(false);
  const [hasPopped, setHasPopped] = useState(false);
  const [hasLiked, setHasLiked] = useState(false);

  if (!isOpen) return null;

  const handlePop = async () => {
    if (hasPopped) return;
    setIsPopping(true);
    try {
      await onPop();
      setHasPopped(true);
      toast({
        title: "🎈 Popped!",
        description: `You popped ${participant.profile?.name}`,
      });
    } finally {
      setIsPopping(false);
    }
  };

  const handleLike = async () => {
    if (hasLiked) return;
    setIsLiking(true);
    try {
      await onLike();
      setHasLiked(true);
      toast({
        title: "❤️ Liked!",
        description: `You liked ${participant.profile?.name}`,
      });
    } finally {
      setIsLiking(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="w-full max-w-md bg-card rounded-t-3xl p-6 animate-in slide-in-from-bottom duration-300">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full bg-muted hover:bg-muted/80 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Profile preview */}
        <div className="flex flex-col items-center mb-6">
          <img
            src={participant.profile?.photo_url || "https://api.dicebear.com/7.x/avataaars/svg?seed=default"}
            alt={participant.profile?.name}
            className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-xl mb-3"
          />
          <h3 className="text-xl font-bold">{participant.profile?.name || "Guest"}</h3>
          <p className="text-sm text-muted-foreground">In this arena</p>
        </div>

        {/* Action buttons */}
        <div className="grid grid-cols-3 gap-3">
          <Button
            variant="outline"
            onClick={onViewProfile}
            className="flex flex-col items-center gap-1 h-auto py-4 rounded-2xl"
          >
            <User className="w-6 h-6 text-muted-foreground" />
            <span className="text-xs">View Profile</span>
          </Button>

          <Button
            variant="outline"
            onClick={handleLike}
            disabled={isLiking || hasLiked}
            className={cn(
              "flex flex-col items-center gap-1 h-auto py-4 rounded-2xl transition-all",
              hasLiked && "bg-pink-50 border-pink-300"
            )}
          >
            <Heart className={cn("w-6 h-6", hasLiked ? "text-pink-500 fill-pink-500" : "text-pink-500")} />
            <span className="text-xs">{hasLiked ? "Liked" : "Like"}</span>
          </Button>

          <Button
            onClick={handlePop}
            disabled={isPopping || hasPopped}
            className={cn(
              "flex flex-col items-center gap-1 h-auto py-4 rounded-2xl",
              hasPopped
                ? "bg-pop/20 text-pop border-pop"
                : "bg-gradient-to-r from-pop to-primary text-white"
            )}
          >
            <Sparkles className="w-6 h-6" />
            <span className="text-xs font-semibold">{hasPopped ? "Popped!" : "Pop 🎈"}</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ParticipantInteractionOverlay;
